import { useMemo, useState, type CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import { type DatesRangeValue } from '@mantine/dates';
import { IoBarChartOutline, IoGridOutline } from 'react-icons/io5';
import { FinanceDateRangeInput } from '@repo/date-range-picker';
import { Breadcrumbs, OptionsTable, StatsBarChart, type OptionsTableColumn } from '@components';
import { QuietIconButton } from '@shared/ui';
import {
    orderCategoryStatsApi,
    type OrderCategoryStatsRow,
} from '@modules/order-category-stats/api/order-category-stats.api';
import { formatPrice } from '@shared/utils';
import '@shared/styles/page.css';
import './OrderCategoryStatsPage.css';

const formatAvgCheck = (revenue: number, ordersCount: number): string => {
    if (ordersCount <= 0) return '—';
    const avg = revenue / ordersCount;
    return new Intl.NumberFormat('ru-RU', {
        style: 'currency',
        currency: 'RUB',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(avg);
};

type StatsView = 'table' | 'chart';

type OrderCategoryStatsTableRow =
    | (OrderCategoryStatsRow & {
          id: string | number;
      })
    | {
          id: '__totals__';
          isTotals: true;
          categoryId: null;
          categoryName: string;
          ordersCount: number;
          itemsSold: number;
          revenue: number;
      };

const totalsRowFont = (item: OrderCategoryStatsTableRow): CSSProperties | undefined =>
    'isTotals' in item && item.isTotals ? { fontWeight: 600 } : undefined;

/** Календарная дата → `YYYY-MM-DD` для API (день по локальному календарю, как в Mantine DatePicker). */
function dateToQueryParam(d: Date | string): string {
    const dt = d instanceof Date ? d : new Date(d);
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const day = String(dt.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

export default function OrderCategoryStatsPage() {
    const [periodRange, setPeriodRange] = useState<DatesRangeValue>([null, null]);
    const [appliedFrom, setAppliedFrom] = useState<string | undefined>();
    const [appliedTo, setAppliedTo] = useState<string | undefined>();
    /** По умолчанию таблица; кнопка справа переключает на график и обратно */
    const [statsView, setStatsView] = useState<StatsView>('table');

    const { data: rows = [], isLoading } = useQuery({
        queryKey: ['order-category-stats', appliedFrom, appliedTo],
        queryFn: () => orderCategoryStatsApi.list({ dateFrom: appliedFrom, dateTo: appliedTo }),
    });

    const onPeriodChange = (range: DatesRangeValue) => {
        setPeriodRange(range);
        const [fromDt, toDt] = range;
        const from = fromDt ? dateToQueryParam(fromDt) : undefined;
        const to = toDt ? dateToQueryParam(toDt) : undefined;
        if (from && to) {
            setAppliedFrom(from <= to ? from : to);
            setAppliedTo(from <= to ? to : from);
            return;
        }
        if (!from && !to) {
            setAppliedFrom(undefined);
            setAppliedTo(undefined);
        }
    };

    /** На графике слева — самые «тяжёлые» по выручке; порядок строк таблицы не меняем. */
    const chartData = useMemo(
        () =>
            [...rows]
                .sort((a, b) => b.revenue - a.revenue)
                .map((r) => ({
                    category: r.categoryName,
                    revenue: r.revenue,
                    ordersCount: r.ordersCount,
                })),
        [rows],
    );

    const totals = rows.reduce(
        (acc, r) => ({
            orders: acc.orders + r.ordersCount,
            items: acc.items + r.itemsSold,
            revenue: acc.revenue + r.revenue,
        }),
        { orders: 0, items: 0, revenue: 0 },
    );

    const tableData = useMemo((): OrderCategoryStatsTableRow[] => {
        if (rows.length === 0) return [];
        const body: OrderCategoryStatsTableRow[] = rows.map((r) => ({
            ...r,
            id: r.categoryId,
        }));
        return [
            ...body,
            {
                id: '__totals__',
                isTotals: true,
                categoryId: null,
                categoryName: 'Сумма по строкам таблицы',
                ordersCount: totals.orders,
                itemsSold: totals.items,
                revenue: totals.revenue,
            },
        ];
    }, [rows, totals.orders, totals.items, totals.revenue]);

    const statsColumns = useMemo<OptionsTableColumn<OrderCategoryStatsTableRow>[]>(
        () => [
            {
                key: 'categoryName',
                label: 'Категория',
                render: (item) => (
                    <span style={totalsRowFont(item)}>{item.categoryName}</span>
                ),
            },
            {
                key: 'ordersCount',
                label: 'Заказов (уник.)',
                render: (item) => <span style={totalsRowFont(item)}>{item.ordersCount}</span>,
            },
            {
                key: 'itemsSold',
                label: 'Позиций, шт.',
                render: (item) => <span style={totalsRowFont(item)}>{item.itemsSold}</span>,
            },
            {
                key: 'revenue',
                label: 'Выручка позиций',
                render: (item) => (
                    <span style={totalsRowFont(item)}>{formatPrice(item.revenue)}</span>
                ),
            },
            {
                key: 'avgCheck',
                label: 'Средний чек',
                render: (item) => (
                    <span style={totalsRowFont(item)}>
                        {'isTotals' in item && item.isTotals
                            ? '—'
                            : formatAvgCheck(item.revenue, item.ordersCount)}
                    </span>
                ),
            },
        ],
        [],
    );

    return (
        <div className="page-container order-category-stats-page">
            <Breadcrumbs
                items={[
                    { label: 'Главная', path: '/' },
                    { label: 'Заказы по категориям' },
                ]}
            />
            <div className="order-category-stats-page-head">
                <h1 className="order-category-stats-page-title">Заказы по категориям</h1>
                <p className="order-category-stats-page-subtitle">
                    Выручка и число заказов по категориям за выбранные даты
                </p>
            </div>

            <div className="order-category-stats-page__filters">
                <span className="order-category-stats-page__period-label">Период</span>
                <div className="order-category-stats-page__period-dates">
                    <FinanceDateRangeInput
                        value={periodRange}
                        onChange={onPeriodChange}
                        valueFormat="DD.MM.YYYY"
                        placeholder="Выберите даты"
                        size="sm"
                        radius="md"
                        popoverProps={{ zIndex: 1100 }}
                    />
                </div>
                {!isLoading && rows.length > 0 && (
                    <QuietIconButton
                        className="order-category-stats-page__view"
                        icon={
                            statsView === 'table' ? (
                                <IoBarChartOutline size={18} />
                            ) : (
                                <IoGridOutline size={18} />
                            )
                        }
                        aria-label={statsView === 'table' ? 'Показать график' : 'Показать таблицу'}
                        title={statsView === 'table' ? 'Показать график' : 'Показать таблицу'}
                        onClick={() => setStatsView((v) => (v === 'table' ? 'chart' : 'table'))}
                    />
                )}
            </div>

            <div className="order-category-stats-page__scroll">
                <div className="content-wrapper order-category-stats-page__content-wrapper">
                    {!isLoading && rows.length > 0 && statsView === 'chart' && (
                        <section className="order-category-stats-card order-category-stats-card--chart">
                            <div className="order-category-stats-card__head">
                                <h2 className="order-category-stats-card__title">Выручка и заказы</h2>
                            </div>
                            <StatsBarChart
                                data={chartData}
                                categoryKey="category"
                                moneySeries={{
                                    dataKey: 'revenue',
                                    legendLabel: 'Выручка',
                                    tooltipTitle: 'Выручка',
                                }}
                                countSeries={{
                                    dataKey: 'ordersCount',
                                    legendLabel: 'Заказов',
                                    tooltipTitle: 'Заказов (уник.)',
                                }}
                            />
                        </section>
                    )}

                    {(isLoading || rows.length === 0 || statsView === 'table') && (
                        <section className="order-category-stats-card order-category-stats-page__table-section">
                            <div className="order-category-stats-page__table-scroll">
                                <OptionsTable<OrderCategoryStatsTableRow>
                                    columns={statsColumns}
                                    data={tableData}
                                    loading={isLoading}
                                    emptyMessage="Нет данных"
                                />
                            </div>
                        </section>
                    )}
                </div>
            </div>
        </div>
    );
}
